import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { AssetGlyph } from "../Brand";
import api, { ASSET_ICONS } from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, X, Copy, ExternalLink } from "lucide-react";

export default function AdminWalletApprovals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/wallets/pending").then((r) => setItems(r.data.wallets || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    const reason = window.prompt("Approval reason (optional):", "Reviewed — looks legitimate");
    if (reason === null) return;
    setBusy(id);
    try {
      const r = await api.post(`/admin/wallets/${id}/approve`, { reason });
      // 🔒 Maker-checker: first call returns 'pending_second_approval' — surface
      // that clearly so the next reviewer knows what to do.
      if (r.data?.status === "pending_second_approval") {
        toast.success("Proposal submitted — awaiting second approver", {
          description: `Proposal ID: ${r.data.proposal_id?.slice(0, 8)}…. A different role-permitted staff member must approve before the wallet is whitelisted.`,
          duration: 8000,
        });
      } else if (r.data?.approved) {
        toast.success("Wallet approved (two-party complete)");
      } else if (r.data?.already_approved) {
        toast.info("Wallet was already approved");
      } else {
        toast.success("Wallet approved");
      }
      load();
    } catch (e) {
      const det = e?.response?.data?.detail || "";
      if (typeof det === "string" && det.startsWith("maker_checker_violation")) {
        toast.error("You proposed this approval — a different reviewer is required.");
      } else {
        toast.error(det || "Approve failed");
      }
    }
    finally { setBusy(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Rejection reason:", "Address format suspicious");
    if (reason === null) return;
    setBusy(id);
    try {
      await api.post(`/admin/wallets/${id}/reject`, { reason });
      toast.success("Wallet rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(null); }
  };

  const copy = (s) => { navigator.clipboard.writeText(s); toast.success("Copied"); };

  return (
    <AdminLayout title="Wallet approvals" subtitle="Stablecoin destinations awaiting staff review (B3 hard gate).">
      <div className="xch-card overflow-hidden" data-testid="wallet-approval-card">
        {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-12 text-center space-y-2" data-testid="wallet-approval-empty">
            <ShieldCheck className="w-10 h-10 mx-auto text-emerald-500/60" />
            <div className="font-display text-lg font-bold text-foreground">Nothing pending</div>
            <div className="text-sm text-muted-foreground">All customer wallets are either approved or rejected.</div>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="divide-y divide-border" data-testid="wallet-approval-list">
            {items.map((w) => (
              <div key={w.id} className="p-4" data-testid={`wallet-approval-row-${w.id}`}>
                <div className="flex items-start gap-3">
                  <AssetGlyph symbol={w.asset} color={ASSET_ICONS[w.asset]} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-bold text-foreground">{w.label || w.asset}</div>
                      <Badge variant="outline" className="rounded-full bg-muted text-[10px] font-bold uppercase tracking-wider">{w.network}</Badge>
                      <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200 text-[10px] font-bold uppercase tracking-wider">Pending</Badge>
                    </div>
                    <button onClick={() => copy(w.address)}
                            className="font-mono text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
                            data-testid={`wallet-address-${w.id}`}>
                      <span className="truncate max-w-[420px]">{w.address}</span>
                      <Copy className="w-3 h-3 shrink-0" />
                    </button>
                    {w.customer && (
                      <div className="text-[11px] text-muted-foreground mt-1.5">
                        Customer: <strong className="text-foreground">{w.customer.name}</strong> · {w.customer.email} · KYC tier {w.customer.kyc_tier ?? 0}
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                      Submitted {new Date(w.created_at).toLocaleString("en-IE")}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button onClick={() => approve(w.id)} disabled={busy === w.id} size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg gap-1"
                            data-testid={`approve-${w.id}`}>
                      <ShieldCheck className="w-3.5 h-3.5" /> Approve
                    </Button>
                    <Button onClick={() => reject(w.id)} disabled={busy === w.id} size="sm"
                            variant="outline"
                            className="border-rose-300 text-rose-700 hover:bg-rose-50 rounded-lg gap-1"
                            data-testid={`reject-${w.id}`}>
                      <X className="w-3.5 h-3.5" /> Reject
                    </Button>
                    <a href={`https://blockchair.com/search?q=${encodeURIComponent(w.address)}`}
                       target="_blank" rel="noopener noreferrer"
                       className="text-[10px] text-primary hover:underline flex items-center gap-1 mt-1"
                       data-testid={`explorer-${w.id}`}>
                      Explorer <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
